import { useState, useEffect, useRef } from "react";
import { apiGet } from "../lib/api";
import { getTeamStartMonth, getTeamMonthCount, getMonthsFrom, shiftMonth } from "../lib/dates";
import { ALL_BLOCKS } from "../lib/blocks";
import { BRAND, PLAYER_COLORS } from "../lib/brand";
import { SmallLogo } from "../components/Logo";
import { MonthNavigator } from "../components/MonthNavigator";
import { CoachCalendarMonth } from "../components/CoachCalendarMonth";
import { DayDetailPanel } from "../components/DayDetailPanel";
import { RosterPanel } from "../components/RosterPanel";
import { ShareLinksPanel } from "../components/ShareLinksPanel";
import { FamilyInvitePanel } from "../components/FamilyInvitePanel";
import { SectionHelp } from "../components/SectionHelp";

/* ───────────────────── COACH VIEW ───────────────────── */
export function CoachView({ team, token, onLogout }) {
  const [allPlayers, setAllPlayers] = useState({});
  // Full roster rows ({ name, role, lastSubmittedAt, ... }), not just staff
  // names. This is the source of truth for WHO exists and WHETHER they've
  // responded — availability rows alone can't answer either question, because
  // "available every day" leaves zero rows (checkpoint_2026-07-28 trap #1).
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoster, setShowRoster] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const invitePanelRef = useRef(null);
  // null = plain "Invite team" open; "remind" = opened from a "nudge the
  // rest" shortcut, which also runs the dry run for you.
  const [inviteFocus, setInviteFocus] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const teamStart = getTeamStartMonth(team);
  const teamCount = getTeamMonthCount(team);
  const [startYear, setStartYear] = useState(teamStart.year);
  const [startMonth, setStartMonth] = useState(teamStart.month);
  const visibleMonths = getMonthsFrom(startYear, startMonth, teamCount);

  const handleShift = (delta) => {
    const s = shiftMonth(startYear, startMonth, delta);
    setStartYear(s.year);
    setStartMonth(s.month);
  };
  const handleReset = () => {
    setStartYear(teamStart.year);
    setStartMonth(teamStart.month);
  };

  // Used by every "nudge the rest" pointer in this screen. Opens the same
  // Invite team panel #5 built (dry run → confirm → send lives there, in
  // FamilyInvitePanel) rather than forking a second reminder UI.
  const openInvitePanel = (focus = null) => {
    setInviteFocus(focus);
    setShowShare(true);
    requestAnimationFrame(() => {
      invitePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  useEffect(() => {
    // Availability is keyed by name and says nothing about who is a coach, or
    // who has responded at all, so the roster is what answers both. A roster
    // fetch that fails leaves the player list built from availability alone —
    // the behavior before this fix — rather than blanking the page.
    Promise.all([
      apiGet("/availability/" + team.id, token),
      apiGet("/roster/" + team.id, token).catch(() => ({ players: [] })),
    ])
      .then(([avail, rosterData]) => {
        setAllPlayers(avail.players || {});
        setRoster(rosterData.players || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [team.id, token]);

  const rosterByName = {};
  roster.forEach((p) => { rosterByName[p.name] = p; });
  const isStaff = (name) => (rosterByName[name]?.role || "player") === "staff";
  const lastSubmittedAt = (name) => rosterByName[name]?.lastSubmittedAt || null;

  // The master name list is the ROSTER — everyone who's supposed to answer —
  // unioned with anyone who has availability rows but no matching roster row
  // (renamed or removed after they replied). A real response must never
  // disappear just because the two tables briefly disagree; a name that's
  // only on the roster still needs to show up so "no reply yet" is visible.
  const submittedNames = Object.keys(allPlayers);
  const rosterNames = roster.map((p) => p.name);
  const allNames = Array.from(new Set([...rosterNames, ...submittedNames])).sort();

  // Every percentage, ratio and conflict count below is about PLAYERS. A coach
  // being away is worth seeing, but it must not make a date look unplayable.
  const playerList = allNames.filter((n) => !isStaff(n));
  const staffList = allNames.filter(isStaff);

  const playerColorMap = {};
  allNames.forEach((name, i) => {
    playerColorMap[name] = PLAYER_COLORS[i % PLAYER_COLORS.length];
  });

  // Parse player data: handle both legacy array and new {date: blocks} format
  function getPlayerDates(name) {
    const raw = allPlayers[name];
    if (!raw) return {};
    if (typeof raw === "object" && !Array.isArray(raw)) return raw; // new: {date: blocksStr}
    if (Array.isArray(raw)) {
      const obj = {};
      raw.forEach((d) => { obj[d] = ALL_BLOCKS.join(","); });
      return obj;
    }
    return {};
  }

  // dateHeat: { dk: [{ name, blocks: string }] } — players only, since it
  // drives the calendar shading and the conflict counts. staffHeat is the same
  // shape for coaches, kept separate so the two never mix.
  const dateHeat = {};
  const staffHeat = {};
  visibleMonths.forEach((m) => {
    for (let d = 1; d <= m.days; d++) {
      const dk = m.year + "-" + String(m.month + 1).padStart(2, "0") + "-" + String(d).padStart(2, "0");
      const collect = (names) => {
        const unavail = [];
        names.forEach((name) => {
          const dates = getPlayerDates(name);
          if (dates[dk]) unavail.push({ name, blocks: dates[dk] });
        });
        return unavail;
      };
      dateHeat[dk] = collect(playerList);
      staffHeat[dk] = collect(staffList);
    }
  });

  // Filtered view for single player selection
  const filteredHeat = selectedPlayer ? {} : dateHeat;
  if (selectedPlayer) {
    Object.entries(dateHeat).forEach(([dk, entries]) => {
      filteredHeat[dk] = entries.filter((e) => e.name === selectedPlayer);
    });
  }
  const filteredPlayerList = selectedPlayer ? [selectedPlayer] : playerList;

  const totalDays = Object.keys(dateHeat).length;

  // Helper: count unavail days for a player
  function playerUnavailCount(name) {
    const dates = getPlayerDates(name);
    return Object.keys(dates).length;
  }

  // Three states, not two. "No rows" and "responded with zero conflicts" look
  // identical in the availability data — that's the whole bug this fixes — so
  // the split has to be keyed off the roster's lastSubmittedAt stamp, not off
  // whether any dates exist.
  function responseState(name) {
    if (!lastSubmittedAt(name)) return "pending";
    return playerUnavailCount(name) === 0 ? "clear" : "marked";
  }

  function respondedDateLabel(name) {
    const iso = lastSubmittedAt(name);
    if (!iso) return "";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  const respondedPlayerCount = playerList.filter((n) => responseState(n) !== "pending").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-white flex items-center justify-center">
        <p className="text-gray-400">Loading team data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <SmallLogo />
            <div>
              <h1 className="text-xl font-extrabold leading-none" style={{ color: BRAND.accent }}>Sartell Soccer</h1>
              <p className="text-xs font-medium" style={{ color: BRAND.primary }}>{team.name} &middot; Coach View</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowShare((v) => !v)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
              {showShare ? "Hide invites" : "Invite team"}
            </button>
            <button onClick={() => setShowRoster((v) => !v)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
              {showRoster ? "Hide roster" : "Edit roster"}
            </button>
            <button onClick={onLogout} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
              Logout
            </button>
          </div>
        </div>

        {/* Getting your team started, without going through the club admin:
            the link and QR code to hand out, and the emails themselves. Every
            send here is scoped to this team by the coach's own token. */}
        {showShare && (
          <div ref={invitePanelRef} className="space-y-6 mb-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-gray-700">Share links &amp; QR codes</h3>
              <SectionHelp label="Which link do I give out?">
                <p>
                  The <strong>parent link</strong> is the one for your families — it opens straight
                  to your team and asks which player they are. Print the QR code for the parent
                  meeting, or paste the link into your team chat.
                </p>
                <p>
                  The <strong>coach link</strong> is this screen. Keep it to yourself: it shows every
                  family's availability.
                </p>
              </SectionHelp>
              <div className="mt-3">
                <ShareLinksPanel team={team} />
              </div>
            </div>

            <FamilyInvitePanel auth={{ token }} teamId={team.id} teamName={team.name} autoFocus={inviteFocus} />
          </div>
        )}

        {/* Coaches can build their own roster — add players and staff, fix a
            name, put an email on a family. Removing someone stays with the club
            admin: it deletes their availability too, and there's no undo. */}
        {showRoster && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Roster</h3>
            <RosterPanel
              teamId={team.id}
              auth={{ token }}
              onChanged={(players) => setRoster(players)}
            />
          </div>
        )}

        {allNames.length === 0 ? (
          // Nobody on the roster at all — nothing to invite yet. Distinct from
          // "roster exists, nobody's answered", which is the next branch: this
          // one points at Edit roster, not Invite team.
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <p className="text-gray-500">No players on the roster yet.</p>
            <p className="mt-2 text-sm text-gray-500">
              Add your players — and any staff — under <strong>Edit roster</strong> above to
              get started.
            </p>
            <button
              onClick={() => setShowRoster(true)}
              className="mt-3 px-5 py-2 text-sm font-semibold text-white rounded-xl shadow-sm"
              style={{ backgroundColor: BRAND.primary }}
            >
              Edit roster
            </button>
          </div>
        ) : playerList.length > 0 && respondedPlayerCount === 0 ? (
          // Roster exists, but no player has answered yet — the "cold start"
          // #5 built the Invite-team CTA for. Checked via respondedPlayerCount,
          // not row-counting, so this correctly clears the moment the FIRST
          // family answers "available every day" and leaves zero rows.
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <p className="text-gray-500">No players have answered yet.</p>
            {/* This used to say "ask the club admin", because a coach session
                never received the PARENT passcode and a link built here would
                have carried an empty code. auth-login now returns both
                passcodes to a coach, so the coach can do it themselves. */}
            <p className="mt-2 text-sm text-gray-500">
              Get them started yourself — <strong>Invite team</strong> up top has your team's
              parent link and QR code, and emails every family a personal link.
            </p>
            <button
              onClick={() => setShowShare(true)}
              className="mt-3 px-5 py-2 text-sm font-semibold text-white rounded-xl shadow-sm"
              style={{ backgroundColor: BRAND.primary }}
            >
              Invite my team
            </button>
            <div>
              <a
                href="/help/coaches"
                className="mt-3 inline-block text-sm font-semibold text-blue-600 hover:text-blue-800"
              >
                How to get families started →
              </a>
            </div>
          </div>
        ) : (
          <>
            {playerList.length > 0 && (
              <p className="text-sm text-gray-500 mb-4 text-center">
                <strong>{respondedPlayerCount} of {playerList.length}</strong> families have answered.
                {respondedPlayerCount < playerList.length && (
                  <>
                    {" "}Need to nudge the rest?{" "}
                    <button
                      onClick={() => openInvitePanel("remind")}
                      className="text-blue-600 font-semibold hover:text-blue-800"
                    >
                      Remind families who haven't replied
                    </button>{" "}
                    — see{" "}
                    <a href="/help/coaches" className="text-blue-600 font-semibold hover:text-blue-800">
                      Help
                    </a>{" "}
                    for details.
                  </>
                )}
              </p>
            )}

            {playerList.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">Players ({playerList.length})</h3>
              <div className="flex flex-wrap gap-2">
                {playerList.map((name) => {
                  const isSelected = selectedPlayer === name;
                  const state = responseState(name);
                  const uCount = playerUnavailCount(name);
                  const pct = Math.round(((totalDays - uCount) / totalDays) * 100);
                  const badgeText =
                    state === "pending" ? "no reply yet" : state === "clear" ? "✓ all available" : pct + "% avail";
                  const badgeClass = isSelected
                    ? "text-white/80"
                    : state === "pending"
                    ? "text-amber-600 font-semibold"
                    : state === "clear"
                    ? "text-green-600 font-semibold"
                    : "text-gray-400";
                  return (
                    <button
                      key={name}
                      onClick={() => { setSelectedPlayer(isSelected ? null : name); setSelectedDay(null); }}
                      style={isSelected ? { backgroundColor: playerColorMap[name], borderColor: playerColorMap[name], color: "#fff" } : {}}
                      className={
                        "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all border-2 " +
                        (isSelected
                          ? "shadow-md"
                          : state === "pending"
                          ? "bg-amber-50 text-gray-700 border-amber-200 hover:border-amber-300"
                          : "bg-white text-gray-700 border-gray-200 hover:border-gray-300")
                      }
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: isSelected ? "#fff" : playerColorMap[name] }} />
                      {name}
                      <span className={"text-xs " + badgeClass}>{badgeText}</span>
                    </button>
                  );
                })}
              </div>
              {selectedPlayer && (
                <p className="text-sm text-gray-500 mt-3 text-center">
                  Showing: <span className="font-semibold" style={{ color: playerColorMap[selectedPlayer] }}>{selectedPlayer}</span>
                  {responseState(selectedPlayer) === "pending" ? (
                    <> &mdash; no reply yet</>
                  ) : (
                    <> &mdash; {playerUnavailCount(selectedPlayer)} days unavailable</>
                  )}
                </p>
              )}
            </div>
            )}

            {/* Coaches and staff, deliberately outside the player numbers. The
                question here isn't "what percent" — it's which dates you're
                short-handed on the sideline. */}
            {staffList.length > 0 && (() => {
              const staffResponded = staffList.filter((n) => responseState(n) !== "pending").length;
              return (
              <div className="bg-white rounded-2xl shadow-sm border border-sky-100 p-4 mb-6">
                <h3 className="text-sm font-semibold text-sky-800 mb-3">
                  Coaches &amp; staff ({staffList.length}) &middot; {staffResponded} of {staffList.length} answered
                </h3>
                <div className="space-y-2">
                  {staffList.map((name) => {
                    const state = responseState(name);
                    const dates = Object.entries(staffHeat)
                      .filter(([, entries]) => entries.some((e) => e.name === name))
                      .map(([dk]) => dk)
                      .sort();
                    return (
                      <div key={name} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: playerColorMap[name] }} />
                        <span className="text-sm font-medium text-gray-700">{name}</span>
                        {state === "pending" ? (
                          <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200">
                            no reply yet
                          </span>
                        ) : dates.length === 0 ? (
                          <span className="text-xs text-green-700 font-semibold">
                            ✓ available every day &middot; responded {respondedDateLabel(name)}
                          </span>
                        ) : (
                          <>
                            <span className="text-xs text-gray-400">{dates.length} day{dates.length !== 1 ? "s" : ""} out:</span>
                            <span className="flex flex-wrap gap-1">
                              {dates.slice(0, 12).map((dk) => (
                                <span key={dk} className="text-xs bg-sky-50 text-sky-800 px-1.5 py-0.5 rounded-full border border-sky-200">
                                  {new Date(dk + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                </span>
                              ))}
                              {dates.length > 12 && (
                                <span className="text-xs text-gray-400">+{dates.length - 12} more</span>
                              )}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })()}

            <MonthNavigator startYear={startYear} startMonth={startMonth} onShift={handleShift} onReset={handleReset} />

            {/* Color legend */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
              <span className="text-xs text-gray-500 font-medium">Legend:</span>
              <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded" style={{backgroundColor:"#dcfce7",border:"1px solid #bbf7d0"}} /> All available</span>
              <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded" style={{backgroundColor:"#ecfdf5",border:"1px solid #d1fae5"}} /> 75%+</span>
              <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded" style={{backgroundColor:"#fef9c3",border:"1px solid #fde68a"}} /> 50%+</span>
              <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded" style={{backgroundColor:"#fee2e2",border:"1px solid #fecaca"}} /> &lt;50%</span>
              <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded" style={{backgroundColor:"#dc2626",border:"1px solid #b91c1c"}} /> None available</span>
              <span className="flex items-center gap-1 text-xs"><span className="text-[10px] font-bold text-gray-500">½</span> Has partial days</span>
              <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded" style={{backgroundColor:"#fef3c7",border:"1px solid #fde68a"}} /> <span className="text-amber-700 font-semibold">No reply yet</span> (player/staff lists, not the calendar)</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {visibleMonths.map((m) => (
                <CoachCalendarMonth
                  key={m.name + m.year}
                  month={m}
                  dateHeat={filteredHeat}
                  playerList={filteredPlayerList}
                  playerColorMap={playerColorMap}
                  selectedDay={selectedDay}
                  onSelectDay={setSelectedDay}
                />
              ))}
            </div>

            <DayDetailPanel
              dateKey={selectedDay}
              dateHeat={dateHeat}
              playerList={playerList}
              playerColorMap={playerColorMap}
              staffHeat={staffHeat}
              staffList={staffList}
            />

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Availability Summary</h3>
              <div className="space-y-3">
                {playerList.map((name) => {
                  const state = responseState(name);
                  const uCount = playerUnavailCount(name);
                  const pct = Math.round(((totalDays - uCount) / totalDays) * 100);
                  return (
                    <div key={name} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: playerColorMap[name] }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-sm font-medium text-gray-700 truncate">{name}</span>
                          {state === "pending" ? (
                            <span className="text-xs text-amber-600 font-semibold ml-2 flex-shrink-0">
                              no reply yet
                            </span>
                          ) : (
                            <span className={"text-xs ml-2 flex-shrink-0 " + (state === "clear" ? "text-green-700 font-semibold" : "text-gray-500")}>
                              {state === "clear"
                                ? "✓ all available · responded " + respondedDateLabel(name)
                                : uCount + " day" + (uCount !== 1 ? "s" : "") + " unavail · " + pct + "% avail"}
                            </span>
                          )}
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className={"h-2 rounded-full transition-all duration-300" + (state === "pending" ? " bg-amber-200" : "")}
                            style={
                              state === "pending"
                                ? { width: "100%" }
                                : { width: pct + "%", backgroundColor: playerColorMap[name] }
                            }
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100">
                <h4 className="text-sm font-semibold text-gray-600 mb-2">Dates All Players Available</h4>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(dateHeat)
                    .filter(([, players]) => players.length === 0)
                    .slice(0, 50)
                    .map(([date]) => {
                      const dt = new Date(date + "T12:00:00");
                      return (
                        <span key={date} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full border border-green-200">
                          {dt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      );
                    })}
                </div>

                {Object.entries(dateHeat).some(([, p]) => p.length >= 3) && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-red-600 mb-2">High Conflict Dates (3+ unavailable)</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(dateHeat)
                        .filter(([, entries]) => entries.length >= 3)
                        .map(([date, entries]) => {
                          const dt = new Date(date + "T12:00:00");
                          const names = entries.map((e) => typeof e === "object" ? e.name : e).join(", ");
                          return (
                            <span key={date} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full border border-red-200"
                              title={names}>
                              {dt.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ({entries.length})
                            </span>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
